import { deleteApi, getApi, postApi, putApi, putUploadImagesApi } from "../helpers/auth_helpers";

const base = process.env.REACT_APP_SERVERURL || "";

export type GiPlace = {
  placeName: string;
  lat: number | string;
  lng: number | string;
};

export const listGis = (params: {
  offset?: number;
  limit?: number;
  status?: string;
  name?: string;
  applicationNumber?: string;
}) => {
  const q = new URLSearchParams();
  q.set("offset", String(params.offset ?? 0));
  q.set("limit", String(params.limit ?? 10));
  if (params.status) q.set("status", params.status);
  if (params.name) q.set("name", params.name);
  if (params.applicationNumber) q.set("applicationNumber", params.applicationNumber);
  return getApi(`${base}/geo/gi?${q.toString()}`);
};

export const getGi = (id: string) => getApi(`${base}/geo/gi/${id}`);

export const createGi = (body: Record<string, unknown>) =>
  postApi(`${base}/geo/gi`, body);

export const updateGi = (id: string, body: Record<string, unknown>) =>
  putApi(`${base}/geo/gi/${id}`, body);

export const freezeGi = (id: string) => postApi(`${base}/geo/gi/${id}/freeze`, {});

export const rejectGi = (id: string, note?: string) =>
  postApi(`${base}/geo/gi/${id}/reject`, { note });

export const approveGiChange = (id: string) =>
  postApi(`${base}/geo/gi/${id}/approve-change`, {});

export const rejectGiChange = (id: string) =>
  postApi(`${base}/geo/gi/${id}/reject-change`, {});

export const previewGiFence = (body: { places?: GiPlace[]; geojson?: Record<string, unknown> }) =>
  postApi(`${base}/geo/gi/preview-fence`, body);

export const getGiSummary = () => getApi(`${base}/geo/gi/summary`);

export const listFrozenGis = () => getApi(`${base}/geo/gi/options`);

export const seedJournalGis = () => postApi(`${base}/geo/gi/seed-journal`, {});

export const listGiCatalog = () => getApi(`${base}/geo/gi/catalog`);

export const adoptGi = (id: string) => postApi(`${base}/geo/gi/${id}/adopt`, {});

export const unadoptGi = (id: string) => deleteApi(`${base}/geo/gi/${id}/adopt`, {});

export const getGiAuthorisedUser = (id: string) =>
  getApi(`${base}/geo/gi/${id}/authorised-user`);

export const saveGiAuthorisedUser = (
  id: string,
  body: {
    authorisedUserNumber: string;
    certificateUrl: string;
    logoUrl: string;
  }
) => putApi(`${base}/geo/gi/${id}/authorised-user`, body);

const GI_PDF_MAX_BYTES = 10 * 1024 * 1024;
const GI_LOGO_MAX_BYTES = 2 * 1024 * 1024;

export const uploadGiPdf = async (file: File): Promise<string> => {
  const mime = file?.type || "application/pdf";
  if (mime !== "application/pdf" && !String(file?.name || "").toLowerCase().endsWith(".pdf")) {
    throw new Error("Upload a PDF file");
  }
  if (file.size > GI_PDF_MAX_BYTES) {
    throw new Error("PDF must be 10 MB or smaller");
  }
  const presigned: any = await postApi(`${base}/upload/geoGiDocumentPresignedURL`, {
    fileType: "application/pdf",
  });
  const presignedUrl: string = presigned?.data;
  if (!presigned?.success || !presignedUrl) {
    throw new Error(presigned?.message || "Could not get upload URL");
  }
  const putRes: any = await putUploadImagesApi(presignedUrl, file, {
    "Content-Type": "application/pdf",
  });
  if (putRes && putRes.ok === false) {
    throw new Error("Could not upload the PDF");
  }
  return String(presignedUrl).split("?")[0];
};

const logoMime = (file: File): string => {
  const mime = file?.type || "";
  const name = String(file?.name || "").toLowerCase();
  if (mime === "image/png" || name.endsWith(".png")) {
    return "image/png";
  }
  if (
    mime === "image/jpeg" ||
    mime === "image/jpg" ||
    name.endsWith(".jpg") ||
    name.endsWith(".jpeg")
  ) {
    return "image/jpeg";
  }
  return "";
};

export const uploadGiLogo = async (file: File): Promise<string> => {
  const mime = logoMime(file);
  if (!mime) {
    throw new Error("Upload a JPEG or PNG logo");
  }
  if (file.size > GI_LOGO_MAX_BYTES) {
    throw new Error("Logo must be 2 MB or smaller");
  }
  const presigned: any = await postApi(`${base}/upload/geoGiDocumentPresignedURL`, {
    fileType: mime,
  });
  const presignedUrl: string = presigned?.data;
  if (!presigned?.success || !presignedUrl) {
    throw new Error(presigned?.message || "Could not get upload URL");
  }
  const putRes: any = await putUploadImagesApi(presignedUrl, file, {
    "Content-Type": mime,
  });
  if (putRes && putRes.ok === false) {
    throw new Error("Could not upload the logo");
  }
  return String(presignedUrl).split("?")[0];
};
